import { initializeApp, deleteApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import {
  getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut, onAuthStateChanged, updatePassword, sendPasswordResetEmail,
} from "firebase/auth";

const firebaseConfig = {
  apiKey:            "AIzaSyAGE2QnlJZXSZX_WSWbNfN396BYtzJ3Dpg",
  authDomain:        "nm-squad.firebaseapp.com",
  projectId:         "nm-squad",
  storageBucket:     "nm-squad.firebasestorage.app",
  messagingSenderId: "819530758546",
  appId:             "1:819530758546:web:e673e635a149e01d15e94b",
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

export async function saveLead(data) {
  const inf = data.info || {};
  return await addDoc(collection(db, "leads"), {
    studentName:       inf.studentName       || "",
    email:             inf.email             || "",
    phone:             inf.phone             || "",
    school:            inf.school            || "",
    parentEmail:       inf.parentEmail       || "",
    parentPhone:       inf.parentPhone       || "",
    country:           inf.country           || "",
    city:              inf.city              || "",
    grade:             inf.grade             || "",
    gender:            inf.gender            || "",
    referral:          inf.referral          || "",
    leadPerson:        inf.leadPerson        || "",
    leadPersonOther:   inf.leadPersonOther   || "",
    token:             data.token            || "",
    IQ:                data.scores?.IQ       || 0,
    EQ:                data.scores?.EQ       || 0,
    DQ:                data.scores?.DQ       || 0,
    AQ:                data.scores?.AQ       || 0,
    dominantProfile:   data.scores?.dominant || "",
    conversionScore:   data.leadScores?.conversionScore   || 0,
    personalScore:     data.leadScores?.personalScore     || 0,
    professionalScore: data.leadScores?.professionalScore || 0,
    status:            "Fresh Enquiry",
    comments:          "",
    interestAnswers:   data.interests  || {},
    parentAnswers:     data.parentAns  || {},
    aiInsights:        data.aiInsights || null,
    createdAt:         new Date(),
  });
}

// DK 8 Oct 2026: NM557139 reached the family by email but never reached "leads" (save ran last and failed
// silently). Save first, retry, and keep a local copy that re-sends on the next page load until it lands.
const PENDING_KEY = "nm_pending_leads";
function readPending() { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || "[]"); } catch { return []; } }
function writePending(list) { try { localStorage.setItem(PENDING_KEY, JSON.stringify(list)); } catch {} }
export async function saveLeadReliable(data) {
  let lastErr;
  for (let i = 0; i < 4; i++) {
    try { return await saveLead(data); }
    catch (e) { lastErr = e; await new Promise(r => setTimeout(r, 1500 * (i + 1))); }
  }
  writePending([...readPending().filter(x => x.token !== data.token), data]);   // retried on the next visit
  throw lastErr;
}
export async function flushPendingLeads() {
  const list = readPending(); if (!list.length) return;
  const left = [];
  for (const d of list) {
    try {
      const q = query(collection(db, "leads"), where("token", "==", d.token));
      const snap = await getDocs(q);
      if (snap.empty) await saveLead(d);
    } catch { left.push(d); }
  }
  writePending(left);
}

export function subscribeLeads(callback) {
  const q = query(collection(db, "leads"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    const leads = snap.docs.map(d => ({ firebaseId: d.id, ...d.data() }));
    callback(leads);
  });
}

export async function updateLeadStatus(firebaseId, status) {
  if (!firebaseId) return;
  await updateDoc(doc(db, "leads", firebaseId), { status });
}

export async function updateLeadComments(firebaseId, comments) {
  if (!firebaseId) return;
  await updateDoc(doc(db, "leads", firebaseId), { comments });
}

export async function saveAIInsights(firebaseId, aiInsights) {
  if (!firebaseId) return;
  await updateDoc(doc(db, "leads", firebaseId), { aiInsights });
}

export async function deleteLead(firebaseId) {
  if (!firebaseId) return;
  await deleteDoc(doc(db, "leads", firebaseId));
}

/* ── WhatsApp auto-send toggle ── */
export async function setWhatsappAutoSend(enabled) {
  await setDoc(doc(db, 'settings', 'whatsapp'), { autoSend: enabled }, { merge: true });
}

export function subscribeWhatsappSettings(callback) {
  return onSnapshot(doc(db, 'settings', 'whatsapp'), (snap) => {
    callback(snap.data() || {});
  });
}

export async function requestWhatsappSend(leadId) {
  await addDoc(collection(db, 'whatsappQueue'), {
    leadId,
    done: false,
    requestedAt: serverTimestamp(),
  });
}

// Post a message into a named WhatsApp group, optionally @-mentioning people.
// The bot (whatsapp-bot/index.js) drains type:'groupMessage' jobs -> sendToGroup.
export async function requestGroupMessage(groupName, text, mentions = []) {
  await addDoc(collection(db, 'whatsappQueue'), {
    type: 'groupMessage',
    groupName,
    text,
    mentions,
    done: false,
    requestedAt: serverTimestamp(),
  });
}

export async function requestWhatsappLogout() {
  await setDoc(doc(db, 'settings', 'whatsapp'), { logoutRequest: true }, { merge: true });
}

/* ── Trusted Partners: vendors ── */
export function subscribeVendors(callback, onError) {
  const q = query(collection(db, "vendors"), orderBy("organization"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, onError);
}

export async function addVendor(data) {
  return await addDoc(collection(db, "vendors"), {
    ...data, status: data.status || "active", portalEnabled: false, createdAt: serverTimestamp(),
  });
}

export async function updateVendor(id, data) {
  if (!id) return;
  await updateDoc(doc(db, "vendors", id), data);
}

/* ── Trusted Partners: referrals (student recommended to a vendor) ── */
export function subscribeReferrals(callback, onError) {
  const q = query(collection(db, "referrals"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, onError);
}

export async function addReferral(data) {
  return await addDoc(collection(db, "referrals"), {
    ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
}

export async function updateReferral(id, data) {
  if (!id) return;
  await updateDoc(doc(db, "referrals", id), { ...data, updatedAt: serverTimestamp() });
}

/* ── Auth ── */
// Behind-the-scenes admin identity so the dashboard keeps full Firestore
// access once rules lock down. Sits behind the existing hardcoded login.
export const ADMIN_AUTH_EMAIL = "admin@nm-squad.firebaseapp.com";
const ADMIN_AUTH_PW = "7ec0c993ebd5cb2c74699483f8b7";

export function onAuth(cb) { return onAuthStateChanged(auth, cb); }
export function signIn(email, pw) { return signInWithEmailAndPassword(auth, (email || "").trim(), pw); }
export function signOutUser() { return signOut(auth); }
export function adminSignIn() { return signInWithEmailAndPassword(auth, ADMIN_AUTH_EMAIL, ADMIN_AUTH_PW); }
// Guarantees an admin auth session before any locked-down read/write.
export async function ensureAdminAuth() {
  if (auth.currentUser && auth.currentUser.email === ADMIN_AUTH_EMAIL) return;
  await signInWithEmailAndPassword(auth, ADMIN_AUTH_EMAIL, ADMIN_AUTH_PW);
}
export function changeMyPassword(newPw) {
  if (!auth.currentUser) return Promise.reject(new Error("Not signed in."));
  return updatePassword(auth.currentUser, newPw);
}

// Create a vendor auth user in an isolated secondary app so the current
// (admin) session is never disturbed.
export async function createVendorUser(email, tempPw) {
  const secApp = initializeApp(firebaseConfig, "vendorCreator-" + Date.now());
  try {
    const secAuth = getAuth(secApp);
    const cred = await createUserWithEmailAndPassword(secAuth, (email || "").trim(), tempPw);
    await signOut(secAuth);
    return cred.user.uid;
  } finally {
    await deleteApp(secApp);
  }
}

/* ── Vendor portal data ── */
function tsMs(t) {
  if (!t) return 0;
  if (t.toDate) return t.toDate().getTime();
  const d = new Date(t);
  return isNaN(d) ? 0 : d.getTime();
}

export async function getVendorAccount(uid) {
  const s = await getDoc(doc(db, "vendorAccounts", uid));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// Vendor first sign-in: create the uid -> vendorId mapping by matching the
// signed-in email to a vendor's portalEmail. Returns null if no vendor matches.
export async function linkVendorAccount(uid, email) {
  const e = (email || "").toLowerCase();
  const qs = await getDocs(query(collection(db, "vendors"), where("portalEmail", "==", e)));
  if (qs.empty) return null;
  const vendorId = qs.docs[0].id;
  const data = { vendorId, email: e, onboarded: false, createdAt: serverTimestamp() };
  await setDoc(doc(db, "vendorAccounts", uid), data);
  return { id: uid, ...data };
}

export function resetPassword(email) {
  return sendPasswordResetEmail(auth, (email || "").trim());
}

/* ── Notifications (vendor inbox) ── */
export async function addNotification(data) {
  return await addDoc(collection(db, "notifications"), { ...data, read: false, createdAt: serverTimestamp() });
}

export function subscribeNotifications(vendorId, callback, onError) {
  const q = query(collection(db, "notifications"), where("vendorId", "==", vendorId));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
    callback(rows);
  }, onError);
}

export async function markNotificationRead(id) {
  if (!id) return;
  await updateDoc(doc(db, "notifications", id), { read: true });
}

export async function getVendorById(id) {
  const s = await getDoc(doc(db, "vendors", id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

export async function saveVendorAccount(uid, data) {
  await setDoc(doc(db, "vendorAccounts", uid), data, { merge: true });
}

export function subscribeVendorReferrals(vendorId, callback, onError) {
  const q = query(collection(db, "referrals"), where("vendorId", "==", vendorId));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
    callback(rows);
  }, onError);
}

// Admin: enable portal access — just flags the vendor with its portalEmail.
// The vendor creates their own login at /vendors; it is matched to this
// vendor by portalEmail on first sign-in. Idempotent.
export async function enableVendorPortal(vendor) {
  const email = (vendor.pocEmail || "").trim();
  if (!email) throw new Error("This vendor has no contact email on file.");
  await updateDoc(doc(db, "vendors", vendor.id), {
    portalEnabled: true, portalEmail: email.toLowerCase(), inviteSentAt: serverTimestamp(),
  });
  return { email };
}

// Vendor self-signup at the portal.
export function signUp(email, pw) {
  return createUserWithEmailAndPassword(auth, (email || "").trim(), pw);
}
